import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  complianceControllerListAlertsQueryKey,
  complianceControllerListAlertsQueryOptions,
  complianceControllerAcknowledgeAlertsByCheckIds,
  complianceControllerUpdateAlert,
  useViewSpecificControllerAcknowledgeComplianceAlert,
  viewSpecificControllerListPolicyComplianceAlertsQueryOptions,
} from 'korint-sdk';
import {
  Check,
  CheckCheck,
  Filter,
  ShieldAlert,
  ShieldCheck,
  Square,
  SquareCheck,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { AlertRow } from './AlertRow';
import { ALERTS_PER_PAGE, type CheckGroup } from './types';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export function EntitySection({
  group,
  policyId,
  canAcknowledge,
}: {
  group: CheckGroup;
  policyId: string;
  canAcknowledge: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<'pending' | 'acknowledged'>('pending');

  const entityId = group.checks[0]?.entityId;

  const alertsQueryParams = {
    entityId,
    target: group.target,
    page,
    limit: ALERTS_PER_PAGE,
    acknowledged: statusFilter === 'acknowledged',
  };

  const { data: alertsResponse, isLoading } = useQuery({
    ...complianceControllerListAlertsQueryOptions(alertsQueryParams),
  });

  // Always fetch unacknowledged count for the header badge
  const { data: pendingCountResponse } = useQuery({
    ...complianceControllerListAlertsQueryOptions({
      entityId,
      target: group.target,
      acknowledged: false,
      limit: 1,
    }),
    select: data => data.totalCount,
  });

  const pendingCount = pendingCountResponse ?? 0;
  const alerts = alertsResponse?.data ?? [];
  const totalCount = alertsResponse?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / ALERTS_PER_PAGE);
  const unacknowledgedOnPage = alerts.filter(a => !a.acknowledged);

  const invalidateAlerts = () => {
    queryClient.invalidateQueries({
      queryKey: complianceControllerListAlertsQueryKey(alertsQueryParams),
    });
    queryClient.invalidateQueries({
      queryKey: complianceControllerListAlertsQueryKey({
        entityId,
        target: group.target,
        acknowledged: false,
        limit: 1,
      }),
    });
    queryClient.invalidateQueries({
      queryKey: viewSpecificControllerListPolicyComplianceAlertsQueryOptions(policyId).queryKey,
    });
  };

  const { mutate: acknowledgeAlert, isPending: isAcknowledging } =
    useViewSpecificControllerAcknowledgeComplianceAlert({
      mutation: { onSuccess: invalidateAlerts },
    });

  const [isBatchAcknowledging, setIsBatchAcknowledging] = useState(false);

  const batchAcknowledgeSelected = async () => {
    setIsBatchAcknowledging(true);
    try {
      await Promise.all(
        Array.from(selectedIds).map(alertId =>
          complianceControllerUpdateAlert(alertId, { acknowledged: true }),
        ),
      );
      setSelectedIds(new Set());
      invalidateAlerts();
    } finally {
      setIsBatchAcknowledging(false);
    }
  };

  const isPending = isAcknowledging || isBatchAcknowledging;

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === unacknowledgedOnPage.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(unacknowledgedOnPage.map(a => a.id)));
    }
  };

  const entityLabel = group.targetLabel;

  return (
    <AccordionItem value={group.target} className="border rounded-lg px-1">
      <AccordionTrigger className="py-3 px-2 hover:no-underline">
        <Group gap={3} className="flex-1">
          {pendingCount > 0 ? (
            <ShieldAlert className="h-4 w-4 text-red-500 shrink-0" />
          ) : (
            <ShieldCheck className="h-4 w-4 text-green-600 shrink-0" />
          )}
          <Text inline size="sm" weight="medium">
            {entityLabel}
          </Text>
          <Group gap={0} className="gap-1.5 ml-auto mr-2">
            {pendingCount > 0 ? (
              <Badge variant="destructive" className="text-xs px-2 py-0">
                {t('compliance.pendingCount', { count: pendingCount })}
              </Badge>
            ) : (
              <Text inline size="xs" weight="medium" className="text-green-700">
                {t('compliance.compliant')}
              </Text>
            )}
          </Group>
        </Group>
      </AccordionTrigger>
      <AccordionContent className="px-2 pb-2">
        {/* Filter bar */}
        <Group gap={0} className="gap-1.5 py-2 px-1 border-b border-border/50">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          {(['pending', 'acknowledged'] as const).map(filter => (
            <button
              key={filter}
              type="button"
              onClick={() => {
                setStatusFilter(filter);
                setPage(1);
                setSelectedIds(new Set());
              }}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                statusFilter === filter
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              {filter === 'pending'
                ? t('compliance.filters.pending')
                : t('compliance.filters.acknowledged')}
            </button>
          ))}
        </Group>

        {isLoading ? (
          <Stack gap={2} className="py-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </Stack>
        ) : alerts.length === 0 ? (
          <div className="py-4 text-center text-sm text-green-700">
            {statusFilter === 'pending'
              ? t('compliance.empty.pending')
              : t('compliance.empty.acknowledged')}
          </div>
        ) : (
          <div>
            {/* Action bar */}
            {canAcknowledge && unacknowledgedOnPage.length > 0 && (
              <Group gap={2} className="py-2 px-1 border-b border-border/50">
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  {selectedIds.size === unacknowledgedOnPage.length ? (
                    <SquareCheck className="h-4 w-4 text-primary" />
                  ) : (
                    <Square className="h-4 w-4" />
                  )}
                </button>
                {selectedIds.size > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs gap-1.5 text-green-700 border-green-200 hover:bg-green-50"
                    onClick={batchAcknowledgeSelected}
                    disabled={isPending}
                  >
                    <Check className="h-3.5 w-3.5" />
                    {t('compliance.actions.acknowledgeSelected', { count: selectedIds.size })}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1.5 ml-auto text-green-700 border-green-200 hover:bg-green-50"
                  onClick={async () => {
                    setIsBatchAcknowledging(true);
                    try {
                      await complianceControllerAcknowledgeAlertsByCheckIds({
                        checkIds: group.checks.map(c => c.id),
                        entityId,
                      });
                      setSelectedIds(new Set());
                      invalidateAlerts();
                    } finally {
                      setIsBatchAcknowledging(false);
                    }
                  }}
                  disabled={isPending}
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  {t('compliance.actions.acknowledgeAll')}
                </Button>
              </Group>
            )}

            {alerts.map(alert => (
              <AlertRow
                key={alert.id}
                alert={alert}
                selected={selectedIds.has(alert.id)}
                onToggleSelect={toggleSelect}
                onAcknowledge={alertId => acknowledgeAlert({ alertId })}
                isPending={isPending}
                canAcknowledge={canAcknowledge}
              />
            ))}

            {totalPages > 1 && (
              <Group justify="between" gap={0} className="pt-3 px-1">
                <Text inline size="xs" tone="muted">
                  {t('compliance.pagination.range', {
                    start: (page - 1) * ALERTS_PER_PAGE + 1,
                    end: Math.min(page * ALERTS_PER_PAGE, totalCount),
                    total: totalCount,
                  })}
                </Text>
                <Group gap={1}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    disabled={page <= 1}
                    onClick={() => {
                      setPage(p => p - 1);
                      setSelectedIds(new Set());
                    }}
                  >
                    {t('compliance.pagination.previous')}
                  </Button>
                  <Text inline size="xs" tone="muted" className="px-1">
                    {page}/{totalPages}
                  </Text>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    disabled={page >= totalPages}
                    onClick={() => {
                      setPage(p => p + 1);
                      setSelectedIds(new Set());
                    }}
                  >
                    {t('compliance.pagination.next')}
                  </Button>
                </Group>
              </Group>
            )}
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}
